import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { createInventoryNode, getInventoryNode, updateInventoryNode, clearInventoryNode } from '../../actions/inventoryNode';
import { getServiceableAreas } from '../../actions/serviceableArea';
import { getSectors } from '../../actions/sector';
import { ArrowLeft, Save, MapPin, Package, Activity, FileText, Building, Monitor, Truck, User, Phone, Mail } from 'lucide-react';

const initialState = {
    name: '',
    type: 'WAREHOUSE',
    address: '',
    location: {
        type: 'Point',
        coordinates: [0, 0]
    },
    sector: '',
    serviceableArea: '',
    totalCapacity: 0,
    capacity: {
        filledCans: 0,
        emptyCans: 0,
        damagedCans: 0
    },
    currentInventory: {
        filledCans: 0,
        emptyCans: 0,
        damagedCans: 0
    },
    contactDetails: {
        name: '',
        phone: '',
        email: ''
    },
    isActive: true,
    parentNode: ''
};

const InventoryNode = ({
    createInventoryNode,
    getInventoryNode,
    updateInventoryNode,
    clearInventoryNode,
    getServiceableAreas,
    getSectors,
    inventoryNode,
    serviceableAreas,
    sectors,
    loading
}) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState(initialState);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [coordinates, setCoordinates] = useState('');

    const isNewNode = id === 'new';
    const nodeTypes = [
        { value: 'WAREHOUSE', label: 'Warehouse', icon: Building },
        { value: 'PLANT', label: 'Plant', icon: Package },
        { value: 'MICRO_WAREHOUSE', label: 'Micro Warehouse', icon: Building },
        { value: 'VENDING_MACHINE', label: 'Vending Machine', icon: Monitor },
        { value: 'VEHICLE', label: 'Vehicle', icon: Truck }
    ];

    useEffect(() => {
        document.title = isNewNode ? 'Create Inventory Node' : 'Edit Inventory Node';
        
        // Load dropdown data
        getServiceableAreas();
        getSectors();
        
        if (!isNewNode && (!inventoryNode || inventoryNode._id !== id)) {
            getInventoryNode(id);
        } else if (!isNewNode && inventoryNode && inventoryNode._id === id) {
            setFormData({ ...initialState, ...inventoryNode });
            
            // Convert coordinates to string format for editing
            if (inventoryNode.location?.coordinates) {
                setCoordinates(`${inventoryNode.location.coordinates[0]},${inventoryNode.location.coordinates[1]}`);
            }
        } else if (isNewNode) {
            clearInventoryNode();
            setFormData(initialState);
            setCoordinates('');
        }
    }, [id, isNewNode, inventoryNode, getInventoryNode, clearInventoryNode, getServiceableAreas, getSectors]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }

        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: type === 'number' ? parseInt(value) || 0 : value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : 
                        type === 'number' ? parseInt(value) || 0 : value
            }));
        }
    };

    const handleCoordinatesChange = (e) => {
        const value = e.target.value;
        setCoordinates(value);
        
        if (errors.coordinates) {
            setErrors(prev => ({ ...prev, coordinates: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Node name is required';
        }

        if (!formData.type) {
            newErrors.type = 'Node type is required';
        }

        if (!formData.address.trim()) {
            newErrors.address = 'Address is required';
        }

        if (!formData.serviceableArea) {
            newErrors.serviceableArea = 'Serviceable area is required';
        }

        // Validate coordinates
        if (!coordinates.trim()) {
            newErrors.coordinates = 'Location coordinates are required';
        } else {
            const [lng, lat] = coordinates.split(',');
            if (isNaN(parseFloat(lng)) || isNaN(parseFloat(lat))) {
                newErrors.coordinates = 'Invalid coordinate format. Use: lng,lat';
            }
        }

        // Validate email if provided
        if (formData.contactDetails.email && 
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactDetails.email)) {
            newErrors['contactDetails.email'] = 'Invalid email format';
        }

        // Validate phone if provided
        if (formData.contactDetails.phone && 
            !/^\d{10}$/.test(formData.contactDetails.phone)) {
            newErrors['contactDetails.phone'] = 'Phone number must be 10 digits';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const parseCoordinates = () => {
        const [lng, lat] = coordinates.split(',');
        return [parseFloat(lng), parseFloat(lat)];
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        try {
            setIsSubmitting(true);
            
            const submissionData = {
                ...formData,
                location: {
                    type: 'Point',
                    coordinates: parseCoordinates()
                }
            };
            
            if (isNewNode) {
                await createInventoryNode(submissionData);
                navigate('/inventory/v1.2/inventory-nodes');
            } else {
                await updateInventoryNode(inventoryNode._id, submissionData);
            }
        } catch (error) {
            console.error('Error submitting form:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading && !isNewNode) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading inventory node details...</p>
                </div>
            </div>
        );
    }

    return (
        <Fragment>
            <div className="container-fluid py-4">
                {/* Header */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <Link
                                className="btn btn-outline-primary d-flex align-items-center"
                                to="/inventory/v1.2/inventory-nodes"
                            >
                                <ArrowLeft size={16} className="me-2" />
                                Back to Inventory Nodes
                            </Link>
                            <h2 className="text-primary mb-0 fw-bold">
                                {isNewNode ? 'Create Inventory Node' : `Edit ${formData.name}`}
                            </h2>
                            <div></div>
                        </div>
                    </div>
                </div>

                <form onSubmit={onSubmit}>
                    <div className="row">
                        {/* Main Form */}
                        <div className="col-lg-8">
                            {/* Basic Information */}
                            <div className="card shadow-sm mb-4">
                                <div className="card-header bg-primary text-white">
                                    <h5 className="mb-0 d-flex align-items-center">
                                        <FileText size={20} className="me-2" />
                                        Basic Information
                                    </h5>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Node Name *</label>
                                            <input
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                                placeholder="Enter node name"
                                            />
                                            {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Node Type *</label>
                                            <select
                                                name="type"
                                                value={formData.type}
                                                onChange={handleChange}
                                                className={`form-select ${errors.type ? 'is-invalid' : ''}`}
                                            >
                                                {nodeTypes.map(type => (
                                                    <option key={type.value} value={type.value}>
                                                        {type.label}
                                                    </option>
                                                ))}
                                            </select>
                                            {errors.type && <div className="invalid-feedback">{errors.type}</div>}
                                        </div>

                                        <div className="col-12 mb-3">
                                            <label className="form-label fw-bold">Address *</label>
                                            <textarea
                                                name="address"
                                                value={formData.address}
                                                onChange={handleChange}
                                                className={`form-control ${errors.address ? 'is-invalid' : ''}`}
                                                placeholder="Enter complete address"
                                                rows="3"
                                            />
                                            {errors.address && <div className="invalid-feedback">{errors.address}</div>}
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Serviceable Area *</label>
                                            <select
                                                name="serviceableArea"
                                                value={formData.serviceableArea?._id || formData.serviceableArea || ''}
                                                onChange={handleChange}
                                                className={`form-select ${errors.serviceableArea ? 'is-invalid' : ''}`}
                                            >
                                                <option value="">Select serviceable area</option>
                                                {serviceableAreas?.map(area => (
                                                    <option key={area._id} value={area._id}>
                                                        {area.name} ({area.city}, {area.state})
                                                    </option>
                                                ))}
                                            </select>
                                            {errors.serviceableArea && <div className="invalid-feedback">{errors.serviceableArea}</div>}
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Sector</label>
                                            <select
                                                name="sector"
                                                value={formData.sector._id || formData.sector || ''}
                                                onChange={handleChange}
                                                className="form-select"
                                            >
                                                <option value="">Select sector (optional)</option>
                                                {sectors?.map(sector => (
                                                    <option key={sector._id} value={sector._id}>
                                                        {sector.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Location Coordinates *</label>
                                            <input
                                                type="text"
                                                value={coordinates}
                                                onChange={handleCoordinatesChange}
                                                className={`form-control ${errors.coordinates ? 'is-invalid' : ''}`}
                                                placeholder="lng,lat (e.g., 77.5946,12.9716)"
                                                style={{ fontFamily: 'monospace' }}
                                            />
                                            {errors.coordinates && <div className="invalid-feedback">{errors.coordinates}</div>}
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Status</label>
                                            <div className="form-check form-switch">
                                                <input
                                                    type="checkbox"
                                                    name="isActive"
                                                    checked={formData.isActive}
                                                    onChange={handleChange}
                                                    className="form-check-input"
                                                    id="isActive"
                                                />
                                                <label className="form-check-label" htmlFor="isActive">
                                                    <Activity size={16} className="me-1" />
                                                    {formData.isActive ? 'Active' : 'Inactive'}
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Capacity Information */}
                            <div className="card shadow-sm mb-4">
                                <div className="card-header bg-info text-white">
                                    <h5 className="mb-0 d-flex align-items-center">
                                        <Package size={20} className="me-2" />
                                        Capacity & Inventory
                                    </h5>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Total Capacity</label>
                                            <input
                                                type="number"
                                                name="totalCapacity"
                                                value={formData.totalCapacity}
                                                onChange={handleChange}
                                                className="form-control"
                                                min="0"
                                                placeholder="Total capacity"
                                            />
                                        </div>
                                    </div>

                                    <h6 className="text-muted mb-3">Capacity Breakdown</h6>
                                    <div className="row">
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Filled Cans Capacity</label>
                                            <input
                                                type="number"
                                                name="capacity.filledCans"
                                                value={formData.capacity.filledCans}
                                                onChange={handleChange}
                                                className="form-control"
                                                min="0"
                                            />
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Empty Cans Capacity</label>
                                            <input
                                                type="number"
                                                name="capacity.emptyCans"
                                                value={formData.capacity.emptyCans}
                                                onChange={handleChange}
                                                className="form-control"
                                                min="0"
                                            />
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Damaged Cans Capacity</label>
                                            <input
                                                type="number"
                                                name="capacity.damagedCans"
                                                value={formData.capacity.damagedCans}
                                                onChange={handleChange}
                                                className="form-control"
                                                min="0"
                                            />
                                        </div>
                                    </div>

                                    <h6 className="text-muted mb-3">Current Inventory</h6>
                                    <div className="row">
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Current Filled Cans</label>
                                            <input
                                                type="number"
                                                name="currentInventory.filledCans"
                                                value={formData.currentInventory.filledCans}
                                                onChange={handleChange}
                                                className="form-control"
                                                min="0"
                                            />
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Current Empty Cans</label>
                                            <input
                                                type="number"
                                                name="currentInventory.emptyCans"
                                                value={formData.currentInventory.emptyCans}
                                                onChange={handleChange}
                                                className="form-control"
                                                min="0"
                                            />
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Current Damaged Cans</label>
                                            <input
                                                type="number"
                                                name="currentInventory.damagedCans"
                                                value={formData.currentInventory.damagedCans}
                                                onChange={handleChange}
                                                className="form-control"
                                                min="0"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Contact Details */}
                            <div className="card shadow-sm mb-4">
                                <div className="card-header bg-warning text-dark">
                                    <h5 className="mb-0 d-flex align-items-center">
                                        <User size={20} className="me-2" />
                                        Contact Details
                                    </h5>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label fw-bold">Contact Person</label>
                                            <div className="input-group">
                                                <span className="input-group-text">
                                                    <User size={16} />
                                                </span>
                                                <input
                                                    type="text"
                                                    name="contactDetails.name"
                                                    value={formData.contactDetails.name}
                                                    onChange={handleChange}
                                                    className="form-control"
                                                    placeholder="Contact person name"
                                                />
                                            </div>
                                        </div>

                                        <div className="col-md-4 mb-3">
                                            <label className="form-label fw-bold">Phone Number</label>
                                            <div className="input-group">
                                                <span className="input-group-text">
                                                    <Phone size={16} />
                                                </span>
                                                <input
                                                    type="tel"
                                                    name="contactDetails.phone"
                                                    value={formData.contactDetails.phone}
                                                    onChange={handleChange}
                                                    className={`form-control ${errors['contactDetails.phone'] ? 'is-invalid' : ''}`}
                                                    placeholder="10-digit phone number"
                                                    maxLength="10"
                                                />
                                                {errors['contactDetails.phone'] && (
                                                    <div className="invalid-feedback">{errors['contactDetails.phone']}</div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="col-md-4 mb-3">
                                            <label className="form-label fw-bold">Email Address</label>
                                            <div className="input-group">
                                                <span className="input-group-text">
                                                    <Mail size={16} />
                                                </span>
                                                <input
                                                    type="email"
                                                    name="contactDetails.email"
                                                    value={formData.contactDetails.email}
                                                    onChange={handleChange}
                                                    className={`form-control ${errors['contactDetails.email'] ? 'is-invalid' : ''}`}
                                                    placeholder="Email address"
                                                />
                                                {errors['contactDetails.email'] && (
                                                    <div className="invalid-feedback">{errors['contactDetails.email']}</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Summary Panel */}
                        <div className="col-lg-4">
                            <div className="card shadow-sm sticky-top">
                                <div className="card-header bg-light">
                                    <h5 className="mb-0">Node Summary</h5>
                                </div>
                                <div className="card-body">
                                    {!isNewNode && (
                                        <div className="mb-3">
                                            <strong>Inventory Node ID:</strong>
                                            <div className="text-muted small" style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                                {id}
                                            </div>
                                        </div>
                                    )}

                                    <div className="mb-3">
                                        <strong>Node Name:</strong>
                                        <div className="text-muted">
                                            {formData.name || <em>Not specified</em>}
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <strong>Coordinates (lng,lat):</strong>
                                        <div className="text-muted small" style={{ fontFamily: 'monospace' }}>
                                            {coordinates || <em>Not specified</em>}
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <strong>Type:</strong>
                                        <div>
                                            <span className="badge bg-primary">
                                                {formData.type.replace('_', ' ')}
                                            </span>
                                        </div>
                                    </div>
                                    
                                    <div className="mb-3">
                                        <strong>Location:</strong>
                                        <div className="text-muted">
                                            {formData.serviceableArea ? (
                                                serviceableAreas?.find(area => 
                                                    area._id === (formData.serviceableArea._id || formData.serviceableArea)
                                                )?.name
                                            ) : (
                                                <em>Not selected</em>
                                            )}
                                        </div>
                                    </div>
                                    
                                    <div className="mb-3">
                                        <strong>Status:</strong>
                                        <div>
                                            <span className={`badge bg-${formData.isActive ? 'success' : 'secondary'}`}>
                                                {formData.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                    </div>
                                    
                                    <div className="mb-3">
                                        <strong>Total Capacity:</strong>
                                        <div className="text-muted">
                                            {formData.totalCapacity} units
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <strong>Current Inventory:</strong>
                                        <div className="text-muted small">
                                            Filled: {formData.currentInventory.filledCans}<br />
                                            Empty: {formData.currentInventory.emptyCans}<br />
                                            Damaged: {formData.currentInventory.damagedCans}
                                        </div>
                                    </div>

                                    {formData.contactDetails.name && (
                                        <div className="mb-3">
                                            <strong>Contact:</strong>
                                            <div className="text-muted small">
                                                {formData.contactDetails.name}
                                                {formData.contactDetails.phone && (
                                                    <><br />{formData.contactDetails.phone}</>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="row">
                        <div className="col-12">
                            <div className="d-flex justify-content-end gap-2">
                                <Link
                                    to="/inventory/v1.2/inventory-nodes"
                                    className="btn btn-outline-secondary"
                                >
                                    Cancel
                                </Link>
                                <button
                                    type="submit"
                                    className="btn btn-primary btn-lg"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <div className="spinner-border spinner-border-sm me-2" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                            {isNewNode ? 'Creating...' : 'Updating...'}
                                        </>
                                    ) : (
                                        <>
                                            <Save size={16} className="me-2" />
                                            {isNewNode ? 'Create Inventory Node' : 'Update Inventory Node'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </Fragment>
    );
};

InventoryNode.propTypes = {
    createInventoryNode: PropTypes.func.isRequired,
    getInventoryNode: PropTypes.func.isRequired,
    updateInventoryNode: PropTypes.func.isRequired,
    clearInventoryNode: PropTypes.func.isRequired,
    getServiceableAreas: PropTypes.func.isRequired,
    getSectors: PropTypes.func.isRequired,
    inventoryNode: PropTypes.object,
    serviceableAreas: PropTypes.array,
    sectors: PropTypes.array,
    loading: PropTypes.bool.isRequired
};

const mapStateToProps = (state) => ({
    inventoryNode: state.inventoryNode.inventoryNode,
    serviceableAreas: state.serviceableArea.serviceableAreas,
    sectors: state.sector.sectors,
    loading: state.inventoryNode.loading
});

export default connect(mapStateToProps, {
    createInventoryNode,
    getInventoryNode,
    updateInventoryNode,
    clearInventoryNode,
    getServiceableAreas,
    getSectors
})(InventoryNode);
